// LabelLayoutDialog.tsx
import { useCallback, useEffect, useState, useRef } from 'react';
import { GridSelector } from './GridSelector';
import './LabelLayoutDialog.css';

// Conversion factors based on the provided JS function.
// The new base unit is PIXELS. Values represent how many of each unit are in 1 pixel.
// For easier calculation, we'll define it as how many pixels are in one of the given units.
const CONVERSION_FACTORS = {
    'pixels': 1,
    'points': 1 / 0.75, // ~1.333 px per point
    'inches': 96, // 96 px per inch
    'millimeters': 1 / 0.2645833333, // ~3.779 px per mm
    'centimeters': 1 / 0.0264583333, // ~37.79 px per cm
};

// Default values, converted to pixels
const DEFAULT_LABEL = {
    labelWidth: 1056,
    labelHeight: 816,
    labelTopMargin: 0,
    labelLeftMargin: 0,
    numOfCols: 1,
    numOfRows: 1,
    horizontalSpace: 0,
    verticalSpace: 0,
    units: 'pixels'
};

const DEFAULT_PAGE = {
    x: 0,
    y: 0,
    width: 1056,
    height: 816
};

interface LabelProps
{
    numOfRows: number;
    numOfCols: number;
    verticalSpace: number;
    horizontalSpace: number;
    labelHeight: number;
    labelWidth: number;
    labelTopMargin: number;
    labelLeftMargin: number;
    units: string;
}

interface PageProperties
{
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ReturnObject
{
    labelProps: LabelProps;
    pageProps: PageProperties;
}

const LABEL_FIELDS_TO_CONVERT: (keyof LabelProps)[] = ['labelWidth', 'labelHeight', 'labelTopMargin', 'labelLeftMargin', 'horizontalSpace', 'verticalSpace'];
const PAGE_FIELD_TO_CONVERT: (keyof PageProperties)[] = ['width', 'height'];

const LabelLayoutDialog = ({ labelProps, pageProps, onClose, onReturnLayout }: {
    labelProps?: Partial<LabelProps>,
    pageProps?: Partial<PageProperties>,
    onClose: () => void,
    onReturnLayout: (modifiedProps: Partial<ReturnObject>) => void
}
) =>
{
    const [retValue, setRetValue] = useState<ReturnObject>({ labelProps: DEFAULT_LABEL, pageProps: DEFAULT_PAGE });
    const formRef = useRef<HTMLFormElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    // Effect to center the dialog when it first renders
    useEffect(() =>
    {
        if (formRef.current)
        {
            const { innerWidth, innerHeight } = window;
            const { offsetWidth, offsetHeight } = formRef.current;
            setPosition({
                x: (innerWidth - offsetWidth) / 2,
                y: (innerHeight - offsetHeight) / 2,
            });
        }
    }, []);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) =>
    {
        if (formRef.current)
        {
            setIsDragging(true);
            setOffset({
                x: e.clientX - formRef.current.offsetLeft,
                y: e.clientY - formRef.current.offsetTop,
            });
        }
    };

    const handleMouseMove = useCallback((e: MouseEvent) =>
    {
        if (isDragging)
        {
            setPosition({
                x: e.clientX - offset.x,
                y: e.clientY - offset.y,
            });
        }
    }, [isDragging, offset]);

    const handleMouseUp = useCallback(() =>
    {
        setIsDragging(false);
    }, []);

    useEffect(() =>
    {
        if (isDragging)
        {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () =>
        {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    useEffect(() =>
    {
        try
        {
            setRetValue({
                labelProps: {
                    ...DEFAULT_LABEL,
                    ...labelProps
                },
                pageProps: {
                    ...DEFAULT_PAGE,
                    ...pageProps
                }
            });
        } catch (error)
        {
            console.log(error);
        }
    }, [labelProps, pageProps]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    {
        try
        {
            const { name, value } = e.target;
            const isNumber = e.target.type === 'number';
            const parsedValue = isNumber ? parseFloat(value) : value;

            // We need to do some special handling since retValue is a nested object
            setRetValue(prev =>
            {
                if (name in prev.pageProps)
                {
                    return {
                        ...prev,
                        pageProps: {
                            ...prev.pageProps,
                            [name]: parsedValue
                        }
                    };
                }
                else if (name in prev.labelProps)
                {
                    return {
                        ...prev,
                        labelProps: {
                            ...prev.labelProps,
                            [name]: parsedValue
                        }
                    };
                }
                return prev;
            });
        } catch (error)
        {
            console.log(error);
        }
    };

    const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    {
        try
        {
            const newUnit = e.target.value as keyof typeof CONVERSION_FACTORS;
            const previousUnit = retValue.labelProps.units as keyof typeof CONVERSION_FACTORS;

            if (newUnit === previousUnit || !CONVERSION_FACTORS[previousUnit] || !CONVERSION_FACTORS[newUnit]) return;

            // Get factors to convert from the previous unit TO pixels and FROM pixels to the new unit.
            const toPixelsFactor = CONVERSION_FACTORS[previousUnit];
            const fromPixelsFactor = CONVERSION_FACTORS[newUnit];

            setRetValue(currentValues =>
            {

                const newLabelProps: LabelProps = { ...currentValues.labelProps, units: newUnit };

                for (const field of LABEL_FIELDS_TO_CONVERT)
                {
                    const valueInPixels = (currentValues.labelProps[field] as number) * toPixelsFactor;
                    const convertedValue = valueInPixels / fromPixelsFactor;
                    (newLabelProps as any)[field] = parseFloat(convertedValue.toFixed(4));
                }

                const newPageProps: PageProperties = { ...currentValues.pageProps };
                for (const field of PAGE_FIELD_TO_CONVERT)
                {
                    const valueInPixels = (currentValues.pageProps[field] as number) * toPixelsFactor;
                    const convertedValue = valueInPixels / fromPixelsFactor;
                    newPageProps[field] = parseFloat(convertedValue.toFixed(4));
                }

                return { labelProps: newLabelProps, pageProps: newPageProps };
            });
        } catch (error)
        {
            console.log(error);
        }
    };

    const handleAutoSeparate = useCallback(() =>
    {
        if (!retValue.pageProps?.width || !retValue.pageProps?.height || !retValue.labelProps)
        {
            alert("Cannot auto-separate without page dimensions.");
            return;
        }

        try
        {
            const currentUnit = retValue.labelProps.units as keyof typeof CONVERSION_FACTORS;
            // Get the factor to convert the current unit to our base unit, pixels
            const toPixelsFactor = CONVERSION_FACTORS[currentUnit];

            // Convert all values to a base unit, which is now pixels
            const lblWidth = retValue.labelProps.labelWidth * toPixelsFactor;
            const lblHeight = retValue.labelProps.labelHeight * toPixelsFactor;
            const topMargin = retValue.labelProps.labelTopMargin * toPixelsFactor;
            const sideMargin = retValue.labelProps.labelLeftMargin * toPixelsFactor;

            // Assuming pageProps are also in the current unit, convert them to pixels
            const pageWidth = retValue.pageProps.width * toPixelsFactor;
            const pageHeight = retValue.pageProps.height * toPixelsFactor;

            const numAcross = retValue.labelProps.numOfCols;
            const numDown = retValue.labelProps.numOfRows;

            const totalWidth = lblWidth * numAcross;
            const totalHeight = lblHeight * numDown;

            if (totalWidth <= pageWidth && totalHeight <= pageHeight)
            {
                // Calculate the printable area in pixels
                const hMargins = 2 * sideMargin;
                const vMargins = 2 * topMargin;
                const printableWidth = pageWidth - hMargins;
                const printableHeight = pageHeight - vMargins;

                // Calculate the gap between labels in pixels
                const hDenominator = (numAcross - 1) === 0 ? 1 : (numAcross - 1);
                const vDenominator = (numDown - 1) === 0 ? 1 : (numDown - 1);

                const hGap = (printableWidth - totalWidth) / hDenominator;
                const vGap = (printableHeight - totalHeight) / vDenominator;

                // Calculate the repeat size in pixels
                const hRepeatInPixels = lblWidth + hGap;
                const vRepeatInPixels = lblHeight + vGap;

                // Convert back to the current display unit and update the state
                setRetValue(prev => ({
                    ...prev,
                    labelProps: {
                        ...prev.labelProps,
                        horizontalSpace: parseFloat((hRepeatInPixels / toPixelsFactor).toFixed(4)),
                        verticalSpace: parseFloat((vRepeatInPixels / toPixelsFactor).toFixed(4)),
                    }
                }));
            } else
            {
                alert("Labels do not fit on the page. Cannot auto-separate.");
            }
        } catch (error)
        {
            console.error("Error during auto-separation:", error);
            alert("An error occurred during auto-separation.");
        }
    }, [retValue]);

    const handleRotate = () =>
    {
        try
        {
            setRetValue(prev => ({
                ...prev,
                labelProps: { ...prev.labelProps },
                pageProps: {
                    ...prev.pageProps,
                    width: prev.pageProps.height,
                    height: prev.pageProps.width,
                },
            }));
        } catch (error)
        {
            console.log(error);
        }
    };

    const handleGridSelectionChange = (rows: number, cols: number) =>
    {
        setRetValue(prev => ({
            ...prev,
            labelProps: {
                ...prev.labelProps,
                numOfRows: rows,
                numOfCols: cols
            },
        }));
    };

    const submit = useCallback((e: React.FormEvent) =>
    {
        try
        {
            e.preventDefault();
            const payload = {
                labelProps: retValue.labelProps,
                pageProps: retValue.pageProps
            };

            onReturnLayout(payload);
        } catch (error)
        {
            console.log(error);
        }
    }, [onReturnLayout, retValue]);

    const cancel = () =>
    {
        try
        {
            setRetValue({
                labelProps: { ...DEFAULT_LABEL, ...labelProps },
                pageProps: { ...DEFAULT_PAGE, ...pageProps }
            });
            onClose();
        } catch (error)
        {
            console.log(error)
        }
    };

    return (
        <div className='ll-container'>
            <form
                ref={formRef}
                onSubmit={submit}
                style={{
                    position: 'absolute',
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                }}
            >
                <div className="ll-drag-handle" onMouseDown={handleMouseDown}>
                    Label Layout
                </div>

                <div className="ll-form-content">
                    <fieldset className='ll-fieldset'>
                        <legend className='ll-legend'>Page Dimensions</legend>
                        <div className='ll-fieldset-content'>
                            <div className='ll-form-row-group'>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='width'>Width:</label>
                                    <input className='ll-input ll-input-narrow' id='width' name='width' type='number' step='any' value={retValue.pageProps.width} onChange={handleInputChange} />
                                </div>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='height'>Height:</label>
                                    <input className='ll-input ll-input-narrow' id='height' name='height' type='number' step='any' value={retValue.pageProps.height} onChange={handleInputChange} />
                                </div>
                                <button type='button' onClick={handleRotate} className='ll-button ll-button-in-group'>Rotate Page</button>
                            </div>
                            <div className='ll-form-row-group'>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='labelTopMargin'>Top Margin:</label>
                                    <input className='ll-input ll-input-narrow' id='labelTopMargin' name='labelTopMargin' type='number' step='any' value={retValue.labelProps.labelTopMargin} onChange={handleInputChange} />
                                </div>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='labelLeftMargin'>Side Margin:</label>
                                    <input className='ll-input ll-input-narrow' id='labelLeftMargin' name='labelLeftMargin' type='number' step='any' value={retValue.labelProps.labelLeftMargin} onChange={handleInputChange} />
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className='ll-fieldset'>
                        <legend className='ll-legend'>Label Layout</legend>
                        <div className='ll-fieldset-content'>
                            <div className='ll-form-row-group'>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='numOfRows'>Rows:</label>
                                    <input className='ll-input ll-input-narrow' id='numOfRows' name='numOfRows' type='number' value={retValue.labelProps.numOfRows} onChange={handleInputChange} />
                                </div>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='numOfCols'>Columns:</label>
                                    <input className='ll-input ll-input-narrow' id='numOfCols' name='numOfCols' type='number' value={retValue.labelProps.numOfCols} onChange={handleInputChange} />
                                </div>
                                <GridSelector rows={retValue.labelProps.numOfRows} cols={retValue.labelProps.numOfCols} onSelectionChange={handleGridSelectionChange} />
                            </div>
                            <div className='ll-form-row-group'>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='horizontalSpace'>Horz. Repeat:</label>
                                    <input className='ll-input ll-input-narrow' id='horizontalSpace' name='horizontalSpace' type='number' step='any' value={retValue.labelProps.horizontalSpace} onChange={handleInputChange} />
                                </div>
                                <div className='ll-input-group'>
                                    <label className='ll-label' htmlFor='verticalSpace'>Vert. Repeat:</label>
                                    <input className='ll-input ll-input-narrow' id='verticalSpace' name='verticalSpace' type='number' step='any' value={retValue.labelProps.verticalSpace} onChange={handleInputChange} />
                                </div>
                                <button type='button' onClick={handleAutoSeparate} className='ll-button ll-button-in-group'>Auto Separate</button>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className='ll-fieldset'>
                        <legend className='ll-legend'>Units of Measure</legend>
                        <div className='ll-fieldset-content'>
                            <div className='ll-form-row ll-units-row'>
                                <select className='ll-select' id='units' name='units' value={retValue.labelProps.units} onChange={handleUnitChange} aria-label='Units of Measure'>
                                    <option value="pixels">Pixels</option>
                                    <option value="points">Points</option>
                                    <option value="inches">Inches</option>
                                    <option value="centimeters">Centimeters</option>
                                    <option value="millimeters">Millimeters</option>
                                </select>
                            </div>
                        </div>
                    </fieldset>

                    <div className='ll-main-buttons'>
                        <button type='submit' className='ll-button ll-ok-button'>OK</button>
                        <button type='button' onClick={cancel} className='ll-button'>Cancel</button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default LabelLayoutDialog;